import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { HibernateFleetSeriesPoint, HibernateSeriesPoint } from "@admin/lib/api";

function formatTick(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `${mm}-${dd} ${hh}:${mi}`;
}

function stateToUp(state: string): number {
  if (state === "running") return 1;
  if (state === "waking") return 0.5;
  return 0;
}

function UserTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: readonly { payload?: HibernateSeriesPoint }[];
  label?: unknown;
}) {
  if (!active || !payload?.[0]?.payload) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-bg/95 px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-medium">{String(label ?? formatTick(point.t))}</div>
      <div>{point.state}</div>
      {point.reason ? <div className="text-muted-fg">reason: {point.reason}</div> : null}
    </div>
  );
}

export function UserHibernateChart({ data }: { data: HibernateSeriesPoint[] }) {
  if (data.length === 0) {
    return <div className="py-10 text-center text-sm text-muted-fg">No hibernation history in this range.</div>;
  }
  const chartData = data.map((p) => ({ ...p, up: stateToUp(p.state) }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
        <XAxis
          dataKey="t"
          tickFormatter={formatTick}
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
          minTickGap={24}
        />
        <YAxis
          domain={[0, 1]}
          ticks={[0, 0.5, 1]}
          tickFormatter={(v: number) => (v === 1 ? "up" : v === 0.5 ? "waking" : "down")}
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={false}
          width={56}
        />
        <Tooltip content={<UserTooltip />} />
        <Area
          type="stepAfter"
          dataKey="up"
          name="Workspace"
          stroke="#34d399"
          fill="#34d399"
          fillOpacity={0.25}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function FleetTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: readonly { name?: unknown; value?: unknown; color?: unknown }[];
  label?: unknown;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-bg/95 px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-medium">{String(label)}</div>
      {payload.map((p) => (
        <div key={String(p.name)} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: typeof p.color === "string" ? p.color : undefined }}
              aria-hidden
            />
            {String(p.name ?? "")}
          </span>
          <span className="tabular-nums">{Number(p.value ?? 0)}</span>
        </div>
      ))}
    </div>
  );
}

export function FleetHibernateChart({ data }: { data: HibernateFleetSeriesPoint[] }) {
  if (data.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-fg">
        No fleet samples in this range yet. Samples start after this deploy.
      </div>
    );
  }
  const chartData = data.map((p) => ({ ...p, tLabel: formatTick(p.t) }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
        <XAxis
          dataKey="tLabel"
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
          minTickGap={24}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: "#a1a1aa" }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <Tooltip content={<FleetTooltip />} />
        <Area
          type="monotone"
          dataKey="runningCount"
          name="Running"
          stackId="fleet"
          stroke="#34d399"
          fill="#34d399"
          fillOpacity={0.35}
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="wakingCount"
          name="Waking"
          stackId="fleet"
          stroke="#fbbf24"
          fill="#fbbf24"
          fillOpacity={0.35}
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="hibernatedCount"
          name="Hibernated"
          stackId="fleet"
          stroke="#818cf8"
          fill="#818cf8"
          fillOpacity={0.35}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
